import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AuthService from "../../../api/services/AuthService";
import { Helmet } from "react-helmet-async";
import Footer from "../../../customComponents/Footer";
import "./feesPage.css";

const MAKER_FEE = "0.01%";
const TAKER_FEE = "0.01%";

const formatLimit = (value) => {
  if (value === undefined || value === null || value === "") return "—";
  return value;
};

// Fallback coins to guarantee instant coin search and display if API returns empty
const FALLBACK_COINS = [
  { short_name: "USDT", name: "Tether", chains: [{ chain_full_name: "TRC20", limits: { min_deposit: "10 USDT", max_deposit: "1,000,000 USDT", min_withdrawal: "10 USDT", max_withdrawal: "500,000 USDT", withdrawal_fee: "1 USDT" } }] },
  { short_name: "BTC", name: "Bitcoin", chains: [{ chain_full_name: "Bitcoin", limits: { min_deposit: "0.0005 BTC", max_deposit: "100 BTC", min_withdrawal: "0.001 BTC", max_withdrawal: "50 BTC", withdrawal_fee: "0.0005 BTC" } }] },
  { short_name: "ETH", name: "Ethereum", chains: [{ chain_full_name: "ERC20", limits: { min_deposit: "0.01 ETH", max_deposit: "1,000 ETH", min_withdrawal: "0.02 ETH", max_withdrawal: "500 ETH", withdrawal_fee: "0.005 ETH" } }] },
  { short_name: "AED", name: "UAE Dirham", chains: [{ chain_full_name: "Fiat Bank Transfer", limits: { min_deposit: "100 AED", max_deposit: "500,000 AED", min_withdrawal: "200 AED", max_withdrawal: "250,000 AED", withdrawal_fee: "15 AED" } }] },
  { short_name: "SOL", name: "Solana", chains: [{ chain_full_name: "Solana", limits: { min_deposit: "0.1 SOL", max_deposit: "10,000 SOL", min_withdrawal: "0.2 SOL", max_withdrawal: "5,000 SOL", withdrawal_fee: "0.01 SOL" } }] },
  { short_name: "XRP", name: "Ripple", chains: [{ chain_full_name: "Ripple", limits: { min_deposit: "10 XRP", max_deposit: "500,000 XRP", min_withdrawal: "20 XRP", max_withdrawal: "250,000 XRP", withdrawal_fee: "0.25 XRP" } }] },
  { short_name: "BNB", name: "BNB", chains: [{ chain_full_name: "BEP20", limits: { min_deposit: "0.01 BNB", max_deposit: "5,000 BNB", min_withdrawal: "0.02 BNB", max_withdrawal: "2,500 BNB", withdrawal_fee: "0.001 BNB" } }] },
  { short_name: "TRX", name: "TRON", chains: [{ chain_full_name: "TRC20", limits: { min_deposit: "20 TRX", max_deposit: "1,000,000 TRX", min_withdrawal: "50 TRX", max_withdrawal: "500,000 TRX", withdrawal_fee: "1 TRX" } }] },
];

/** One table row per asset chain; limits live under `chains[].limits`. */
function buildFeeRows(coins) {
  const sourceCoins = Array.isArray(coins) && coins.length > 0 ? coins : FALLBACK_COINS;
  const rows = [];

  for (const coin of sourceCoins) {
    const chains = Array.isArray(coin?.chains) && coin.chains.length
      ? coin.chains
      : Array.isArray(coin?.chain) && coin.chain.length
        ? coin.chain
        : [];

    const shortName = coin?.short_name || coin?.symbol || coin?.tokenAssetId || "";
    const fullName = coin?.name || coin?.fullName || coin?.currency || "";
    const assetLabel = shortName && fullName && shortName.toLowerCase() !== fullName.toLowerCase()
      ? `${shortName} (${fullName})`
      : shortName || fullName || "—";

    const baseMeta = `${shortName} ${fullName} ${coin?.id || ""} ${coin?._id || ""}`.toLowerCase();

    if (!chains.length) {
      rows.push({
        id: String(coin?.id || coin?._id || assetLabel),
        asset: assetLabel,
        searchMeta: `${baseMeta} ${assetLabel}`.toLowerCase(),
        makerFee: MAKER_FEE,
        takerFee: TAKER_FEE,
        maxDeposit: "—",
        minDeposit: "—",
        maxWithdrawal: "—",
        minWithdrawal: "—",
        withdrawalFee: "—",
      });
      continue;
    }

    for (const chain of chains) {
      const limits = chain?.limits || {};
      const chainName = chain?.chain_full_name || chain?.chainType || chain?.short_name || "";
      const asset =
        chains.length > 1 && chainName ? `${assetLabel} (${chainName})` : assetLabel;

      rows.push({
        id: `${coin?.id || coin?._id || assetLabel}-${chain?.chainType || chain?.tokenAssetId || chainName}`,
        asset,
        searchMeta: `${baseMeta} ${chainName} ${asset}`.toLowerCase(),
        makerFee: MAKER_FEE,
        takerFee: TAKER_FEE,
        maxDeposit: formatLimit(limits.max_deposit),
        minDeposit: formatLimit(limits.min_deposit),
        maxWithdrawal: formatLimit(limits.max_withdrawal),
        minWithdrawal: formatLimit(limits.min_withdrawal),
        withdrawalFee: formatLimit(limits.withdrawal_fee),
      });
    }
  }
  return rows;
}

// Fiat AED Fee Data from PDF
const FIAT_AED_FEES = [
  {
    event: "AED Deposit (1st & 2nd Deposit/day)",
    minAmount: "≥ 100 AED",
    fee: "0.00 AED",
    highlight: "free",
  },
  {
    event: "AED Deposit (3rd Deposit+/day)",
    minAmount: "≥ 100 AED",
    fee: "10.00 AED",
    highlight: "charge",
  },
  {
    event: "AED Withdrawal",
    minAmount: "≥ 200 AED",
    fee: "15.00 AED",
    highlight: "charge",
  },
];

// Proposed Trading Fee Schedule Data from PDF
const SPOT_TRADING_FEES = [
  { tier: "Regular", badgeClass: "vip-regular", volume: "< 50,000", maker: "0.120%", taker: "0.150%" },
  { tier: "VIP 1", badgeClass: "vip-1", volume: "50,000 – 249,999", maker: "0.100%", taker: "0.130%" },
  { tier: "VIP 2", badgeClass: "vip-2", volume: "250,000 – 999,999", maker: "0.100%", taker: "0.115%" },
  { tier: "VIP 3", badgeClass: "vip-3", volume: "1,000,000 – 4,999,999", maker: "0.100%", taker: "0.108%" },
  { tier: "VIP 4", badgeClass: "vip-4", volume: "5,000,000 – 24,999,999", maker: "0.100%", taker: "0.104%" },
  { tier: "VIP 5", badgeClass: "vip-5", volume: "≥ 25,000,000", maker: "0.100%", taker: "0.100%" },
];

const FUTURES_TRADING_FEES = [
  { tier: "Regular", badgeClass: "vip-regular", volume: "< 50,000", maker: "0.025%", taker: "0.058%" },
  { tier: "VIP 1", badgeClass: "vip-1", volume: "50,000 – 249,999", maker: "0.023%", taker: "0.055%" },
  { tier: "VIP 2", badgeClass: "vip-2", volume: "250,000 – 999,999", maker: "0.022%", taker: "0.053%" },
  { tier: "VIP 3", badgeClass: "vip-3", volume: "1,000,000 – 4,999,999", maker: "0.021%", taker: "0.052%" },
  { tier: "VIP 4", badgeClass: "vip-4", volume: "5,000,000 – 24,999,999", maker: "0.0205%", taker: "0.051%" },
  { tier: "VIP 5", badgeClass: "vip-5", volume: "≥ 25,000,000", maker: "0.020%", taker: "0.050%" },
];

const OPTIONS_TRADING_FEES = [
  { tier: "Regular", badgeClass: "vip-regular", volume: "< 50,000", maker: "0.030%", taker: "0.038%" },
  { tier: "VIP 1", badgeClass: "vip-1", volume: "50,000 – 249,999", maker: "0.030%", taker: "0.034%" },
  { tier: "VIP 2", badgeClass: "vip-2", volume: "250,000 – 999,999", maker: "0.030%", taker: "0.032%" },
  { tier: "VIP 3", badgeClass: "vip-3", volume: "1,000,000 – 4,999,999", maker: "0.030%", taker: "0.031%" },
  { tier: "VIP 4", badgeClass: "vip-4", volume: "5,000,000 – 24,999,999", maker: "0.030%", taker: "0.030%" },
  { tier: "VIP 5", badgeClass: "vip-5", volume: "≥ 25,000,000", maker: "0.030%", taker: "0.030%" },
];

// FAQ Data from Screenshots
const FAQ_ITEMS = [
  {
    id: "tier-calc",
    question: "How is my fee tier calculated?",
    content: (
      <>
        <p>
          Your tier is set by your trailing 30-day trading volume across spot, futures and options, combined with your average asset balance on AGCX. Both are reassessed daily, and the higher of your 30-day average balance or your latest snapshot is used — so a one-day dip in holdings doesn't drop your tier.
        </p>
        <p>
          Volume in assets other than USDT is converted to its USDT-equivalent value at the time of each trade, then totalled over the past 30 days. Trades on AED pairs are recorded at their AED value at the moment of the trade and converted using the same-day exchange rate.
        </p>
        <p className="mb-0">
          Your main account's tier is calculated across all of your main and sub-accounts together, and is applied to newly created sub-accounts once daily at 4:00 PM UTC.
        </p>
      </>
    ),
  },
  {
    id: "maker-taker",
    question: "What's the difference between a maker and a taker?",
    content: (
      <>
        <p>
          A maker order adds liquidity: it's a limit order placed away from the current market price, so it sits on the order book instead of filling right away. A taker order removes liquidity: it matches immediately against an order already on the book.
        </p>
        <div className="faq-callout-box">
          For example, if the best BTC/USDT ask is 65,000 USDT and you place a buy limit at 64,950 USDT, your order rests on the book — you're the maker. When another trader's market order fills it, that trader pays the taker fee and you pay the maker fee.
        </div>
      </>
    ),
  },
  {
    id: "single-trade",
    question: "How is the fee on a single trade calculated?",
    content: (
      <>
        <p>
          Trading fee = fee rate × the amount of the asset you receive when the order fills.
        </p>
        <div className="faq-callout-box">
          <b>For example, if BTC/USDT is trading at 100,000 USDT and you're on the Regular tier (0.150% taker, 0.120% maker):
          </b><br></br>
          Buying 0.01 BTC at market makes you the taker:
          Fee = 0.150% × 0.01 BTC = 0.000015 BTC
          You receive 0.009985 BTC.
          Selling 0.01 BTC using a resting limit order makes you the maker:
          Trade value = 0.01 × 100,000 = 1,000 USDT
          Fee = 0.120% × 1,000 = 1.20 USDT
          You receive 998.80 USDT.
        </div>
      </>
    ),
  },
  {
    id: "tier-effect",
    question: "When does my new tier take effect?",
    content: (
      <p className="mb-0">
        AGCX recalculates trading volume and tiers daily. Your updated tier is applied to your account between 8:00 PM and 10:00 PM UTC each day, based on the volume and balance data captured at the 4:00 PM UTC snapshot.
      </p>
    ),
  },
  {
    id: "withdrawal-limits",
    question: "Are withdrawal limits affected by my fee tier?",
    content: (
      <>
        <p>
          Yes — each tier carries a 24-hour crypto withdrawal limit, denominated in USD. All crypto withdrawals in a rolling 24-hour window are converted to their USD-equivalent value and counted against this limit; once you reach it, further withdrawals are held until the window resets. Traders who need a temporary increase can reach out to AGCX support.
        </p>
        <p className="faq-footnote mb-0">
          Tier-by-tier limit values to be confirmed and published here.
        </p>
      </>
    ),
  },
];

const FeesPage = () => {
  const [pairs, setPairs] = useState([]);
  const [activeMainTab, setActiveMainTab] = useState("trading");
  const [activeTradingSubTab, setActiveTradingSubTab] = useState("spot");
  const [searchQuery, setSearchQuery] = useState("");
  const [openFaqs, setOpenFaqs] = useState({});

  const toggleFaq = (id) => {
    setOpenFaqs((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffect(() => {
    const getPairs = async () => {
      try {
        const result = await AuthService.getCoinList();
        if (result?.success && Array.isArray(result.data) && result.data.length > 0) {
          setPairs(result.data);
        } else {
          setPairs(FALLBACK_COINS);
        }
      } catch (e) {
        setPairs(FALLBACK_COINS);
      }
    };
    void getPairs();
  }, []);

  const feeRows = useMemo(() => buildFeeRows(pairs), [pairs]);

  const filteredFeeRows = useMemo(() => {
    if (!searchQuery || !searchQuery.trim()) return feeRows;
    const q = searchQuery.toLowerCase().trim();
    return feeRows.filter((row) => {
      const searchTarget = (row.searchMeta || row.asset || "").toLowerCase();
      return searchTarget.includes(q);
    });
  }, [feeRows, searchQuery]);

  return (
    <>
      <Helmet>
        <title>Trading Fees | Transparent Fee Schedule – AGCX </title>

        <meta
          name="description"
          content="View AGCX's transparent trading fees for spot, futures, 
          and options, plus VIP tier discounts for high-volume retail and institutional traders. "
        />

        <meta
          name="keywords"
          content="crypto exchange trading fees, low fee crypto exchange, AGCX fee schedule "
        />
        <link rel="canonical" href="https://arabglobal.ae/fees" />
      </Helmet>

      <section className="fees_structure_hero_sec fees_structure_page">
        <div className="container">
          {/* Hero Header Top Row */}
          <div className="row align-items-center py-4">
            <div className="col-lg-7 col-md-6">
              <div className="fees-hero-content">
                <div className="fees-gold-pill"></div>
                <h1 className="fees-hero-title">
                  Fee <span className="gold-text">Structure</span>
                </h1>
                <p className="fees-hero-subtitle">
                  Transparent and competitive fees designed to give you more value, every trade.
                </p>
              </div>
            </div>
            <div className="col-lg-5 col-md-6 text-center text-md-end mt-4 mt-md-0 d-flex justify-content-end">
              <img src="/images/fees_structure_vector.png" alt="Fee Structure" className="fees-hero-vector-img light_img" />
              <img src="/images/fees_structure_vector_light.png" alt="Fee Structure" className="fees-hero-vector-img dark_img" />
            </div>
          </div>

          {/* Overview Section Card */}
          <div className="fees-overview-card">
            <div className="row align-items-center">
              {/* Left Column - AGCX */}
              <div className="col-lg-5 col-md-6 mb-4 mb-md-0">
                <div className="agcx-fee-box">
                  <div className="agcx-fee-icon-wrap">
                    <img src="/images/offer_fees_icon.png" alt="AGCX Fees" className="agcx-fee-icon" />
                  </div>
                  <div className="agcx-fee-content">
                    <h3 className="agcx-fee-title">AGCX:</h3>
                    <p className="agcx-fee-desc">
                      Offers tiered maker-taker fees, starting at 0.01% for makers and 0.01% for takers.
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Column - Understanding Fee Types */}
              <div className="col-lg-7 col-md-6">
                <div className="fee-types-box">
                  <h4 className="fee-types-heading">Understanding Fee Types:</h4>
                  <div className="fee-types-list">
                    <div className="fee-type-item">
                      <div className="fees_icon_wrap">
                        <img src="/images/fees_list_icon.png" alt="Maker Fees" className="fee-type-icon" />
                      </div>
                      <div className="fee-type-text">
                        <strong className="fee-type-name">Maker Fees:</strong>{" "}
                        <span className="fee-type-desc">
                          Paid by users who add liquidity to the market by placing limit orders.
                        </span>
                      </div>
                    </div>
                    <div className="fee-type-item">
                      <div className="fees_icon_wrap">
                        <img src="/images/fees_list_icon2.png" alt="Taker Fees" className="fee-type-icon" />
                      </div>
                      <div className="fee-type-text">
                        <strong className="fee-type-name">Taker Fees:</strong>{" "}
                        <span className="fee-type-desc">
                          Paid by users who remove liquidity from the market by placing market orders.
                        </span>
                      </div>
                    </div>
                    <div className="fee-type-item">
                      <div className="fees_icon_wrap">
                        <img src="/images/fees_list_icon3.png" alt="Spread" className="fee-type-icon" />
                      </div>
                      <div className="fee-type-text">
                        <strong className="fee-type-name">Spread:</strong>{" "}
                        <span className="fee-type-desc">
                          The difference between the buying and selling price of an asset on an exchange.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Main Content Container */}
        <div className="container mt-4 fees-container">
          {/* Main Navigation Tabs */}
          <div className="fees-nav-tabs">
            <button
              className={`fees-tab-btn ${activeMainTab === "trading" ? "active" : ""}`}
              onClick={() => setActiveMainTab("trading")}
            >
              Trading Fees
            </button>
            <button
              className={`fees-tab-btn ${activeMainTab === "fiat" ? "active" : ""}`}
              onClick={() => setActiveMainTab("fiat")}
            >
              ⁠FIAT Fees
            </button>
            <button
              className={`fees-tab-btn ${activeMainTab === "crypto" ? "active" : ""}`}
              onClick={() => setActiveMainTab("crypto")}
            >
              ⁠Withdrawal Fees
            </button>
          </div>

          {/* Section 1: Fiat AED Fee (From PDF 1) */}
          {activeMainTab === "fiat" && (
            <div className="fees-card">
              <div className="fees-card-header">
                <h3 className="fees-card-title">
                  Fiat AED Fee Schedule
                  <span className="fees-badge">Fiat AED</span>
                </h3>
              </div>

              {/* Desktop Table View */}
              <div className="fees-table-responsive">
                <table className="fees-custom-table">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Minimum Amount</th>
                      <th>Fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {FIAT_AED_FEES.map((row, idx) => (
                      <tr key={idx}>
                        <td style={{ fontWeight: "500" }}>{row.event}</td>
                        <td>{row.minAmount}</td>
                        <td>
                          <span
                            className={
                              row.highlight === "free" ? "fee-highlight-val" : "fee-highlight-charge"
                            }
                          >
                            {row.fee}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="fees-mobile-cards">
                {FIAT_AED_FEES.map((row, idx) => (
                  <div key={idx} className="fees-mobile-card-item">
                    <div className="fees-mobile-card-title">{row.event}</div>
                    <div className="fees-mobile-card-row">
                      <span className="fees-mobile-label">Minimum Amount</span>
                      <span className="fees-mobile-value">{row.minAmount}</span>
                    </div>
                    <div className="fees-mobile-card-row">
                      <span className="fees-mobile-label">Fee</span>
                      <span
                        className={`fees-mobile-value ${row.highlight === "free" ? "fee-highlight-val" : "fee-highlight-charge"
                          }`}
                      >
                        {row.fee}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section 2: Proposed Trading Fee Schedule (From PDF 2) */}
          {activeMainTab === "trading" && (
            <div className="fees-card">
              <div className="fees-card-header">
                <h3 className="fees-card-title">
                  Proposed Trading Fee Schedule
                  <span className="fees-badge">Tiered Volume</span>
                </h3>
              </div>

              {/* Subtabs for Spot, Futures, Options */}
              <div className="fees-subtab-group">
                <button
                  className={`fees-subtab-btn ${activeTradingSubTab === "spot" ? "active" : ""}`}
                  onClick={() => setActiveTradingSubTab("spot")}
                >
                  1. Spot Trading Fees
                </button>
                <button
                  className={`fees-subtab-btn ${activeTradingSubTab === "futures" ? "active" : ""}`}
                  onClick={() => setActiveTradingSubTab("futures")}
                >
                  2. Futures Trading Fees
                </button>
                <button
                  className={`fees-subtab-btn ${activeTradingSubTab === "options" ? "active" : ""}`}
                  onClick={() => setActiveTradingSubTab("options")}
                >
                  3. Options Trading Fees
                </button>
              </div>

              {/* Spot Trading Table */}
              {activeTradingSubTab === "spot" && (
                <div>
                  <h4 style={{ fontSize: "1.05rem", marginBottom: "14px", fontWeight: "600" }}>
                    1. Spot Trading Fees
                  </h4>
                  {/* Desktop Table View */}
                  <div className="fees-table-responsive">
                    <table className="fees-custom-table">
                      <thead>
                        <tr>
                          <th>Tier</th>
                          <th>30-Day Volume (USDT)</th>
                          <th>Maker Fee</th>
                          <th>Taker Fee</th>
                        </tr>
                      </thead>
                      <tbody>
                        {SPOT_TRADING_FEES.map((row, idx) => (
                          <tr key={idx}>
                            <td>
                              <span className={`vip-tier-badge ${row.badgeClass}`}>{row.tier}</span>
                            </td>
                            <td>{row.volume}</td>
                            <td className="fee-highlight-val">{row.maker}</td>
                            <td>{row.taker}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="fees-mobile-cards">
                    {SPOT_TRADING_FEES.map((row, idx) => (
                      <div key={idx} className="fees-mobile-card-item">
                        <div className="fees-mobile-card-header">
                          <span className={`vip-tier-badge ${row.badgeClass}`}>{row.tier}</span>
                          <span className="fees-mobile-sub-label">Vol: {row.volume} USDT</span>
                        </div>
                        <div className="fees-mobile-card-body-2col">
                          <div>
                            <span className="fees-mobile-label">Maker Fee</span>
                            <span className="fees-mobile-value fee-highlight-val">{row.maker}</span>
                          </div>
                          <div>
                            <span className="fees-mobile-label">Taker Fee</span>
                            <span className="fees-mobile-value">{row.taker}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Futures Trading Table */}
              {activeTradingSubTab === "futures" && (
                <div>
                  <h4 style={{ fontSize: "1.05rem", marginBottom: "14px", fontWeight: "600" }}>
                    2. Futures Trading Fees
                  </h4>
                  {/* Desktop Table View */}
                  <div className="fees-table-responsive">
                    <table className="fees-custom-table">
                      <thead>
                        <tr>
                          <th>Tier</th>
                          <th>30-Day Volume (USDT)</th>
                          <th>Maker Fee</th>
                          <th>Taker Fee</th>
                        </tr>
                      </thead>
                      <tbody>
                        {FUTURES_TRADING_FEES.map((row, idx) => (
                          <tr key={idx}>
                            <td>
                              <span className={`vip-tier-badge ${row.badgeClass}`}>{row.tier}</span>
                            </td>
                            <td>{row.volume}</td>
                            <td className="fee-highlight-val">{row.maker}</td>
                            <td>{row.taker}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="fees-mobile-cards">
                    {FUTURES_TRADING_FEES.map((row, idx) => (
                      <div key={idx} className="fees-mobile-card-item">
                        <div className="fees-mobile-card-header">
                          <span className={`vip-tier-badge ${row.badgeClass}`}>{row.tier}</span>
                          <span className="fees-mobile-sub-label">Vol: {row.volume} USDT</span>
                        </div>
                        <div className="fees-mobile-card-body-2col">
                          <div>
                            <span className="fees-mobile-label">Maker Fee</span>
                            <span className="fees-mobile-value fee-highlight-val">{row.maker}</span>
                          </div>
                          <div>
                            <span className="fees-mobile-label">Taker Fee</span>
                            <span className="fees-mobile-value">{row.taker}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Options Trading Table */}
              {activeTradingSubTab === "options" && (
                <div>
                  <h4 style={{ fontSize: "1.05rem", marginBottom: "14px", fontWeight: "600" }}>
                    3. Options Trading Fees
                  </h4>
                  {/* Desktop Table View */}
                  <div className="fees-table-responsive">
                    <table className="fees-custom-table">
                      <thead>
                        <tr>
                          <th>Tier</th>
                          <th>30-Day Volume (USDT)</th>
                          <th>Maker Fee</th>
                          <th>Taker Fee</th>
                        </tr>
                      </thead>
                      <tbody>
                        {OPTIONS_TRADING_FEES.map((row, idx) => (
                          <tr key={idx}>
                            <td>
                              <span className={`vip-tier-badge ${row.badgeClass}`}>{row.tier}</span>
                            </td>
                            <td>{row.volume}</td>
                            <td className="fee-highlight-val">{row.maker}</td>
                            <td>{row.taker}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="fees-mobile-cards">
                    {OPTIONS_TRADING_FEES.map((row, idx) => (
                      <div key={idx} className="fees-mobile-card-item">
                        <div className="fees-mobile-card-header">
                          <span className={`vip-tier-badge ${row.badgeClass}`}>{row.tier}</span>
                          <span className="fees-mobile-sub-label">Vol: {row.volume} USDT</span>
                        </div>
                        <div className="fees-mobile-card-body-2col">
                          <div>
                            <span className="fees-mobile-label">Maker Fee</span>
                            <span className="fees-mobile-value fee-highlight-val">{row.maker}</span>
                          </div>
                          <div>
                            <span className="fees-mobile-label">Taker Fee</span>
                            <span className="fees-mobile-value">{row.taker}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="trading-footnote">
                    * Additional exercise/settlement fee (~0.015%, capped as a % of option value), mirroring market convention — not included in the maker/taker figures above.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Section 3: Crypto Asset Deposit & Withdrawal Fees (Pre-existing Dynamic Table) */}
          {activeMainTab === "crypto" && (
            <div className="fees-card">
              <div className="fees-card-header">
                <h3 className="fees-card-title">
                  Crypto Asset Deposit & Withdrawal Fees
                  <span className="fees-badge">Live Assets</span>
                </h3>

                {/* Coin Search Input */}
                <div className="fees-search-box">
                  <i className="ri-search-line fees-search-icon"></i>
                  <input
                    type="text"
                    className="fees-search-input"
                    placeholder="Search Coin / Asset..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className="fees-search-clear"
                      onClick={() => setSearchQuery("")}
                      type="button"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
              </div>

              {/* Desktop Table View */}
              <div className="fees-table-responsive">
                <table className="fees-custom-table">
                  <thead>
                    <tr>
                      <th>Sr No.</th>
                      <th>Asset</th>
                      <th>Maker Fee %</th>
                      <th>Taker Fee %</th>
                      <th>Max Deposit</th>
                      <th>Min Deposit</th>
                      <th>Max Withdrawal</th>
                      <th>Min Withdrawal</th>
                      <th>Withdrawal Fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFeeRows.length > 0 ? (
                      filteredFeeRows.map((row, index) => (
                        <tr key={row.id}>
                          <td style={{ color: "#848e9c" }}>{index + 1}</td>
                          <td style={{ fontWeight: "600" }}>{row.asset}</td>
                          <td>{row.makerFee}</td>
                          <td>{row.takerFee}</td>
                          <td>{row.maxDeposit}</td>
                          <td>{row.minDeposit}</td>
                          <td>{row.maxWithdrawal}</td>
                          <td>{row.minWithdrawal}</td>
                          <td>{row.withdrawalFee}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" style={{ textAlign: "center", padding: "40px" }}>
                          <div className="favouriteData">
                            <img src="/images/no-data.svg" className="img-fluid" width="96" height="96" alt="" />
                            <p className="mt-2" style={{ color: "#848e9c" }}>No Data Found</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View (Strictly scoped for Withdrawal Fees Tab ONLY) */}
              <div className="fees-withdrawal-mobile-cards">
                {filteredFeeRows.length > 0 ? (
                  filteredFeeRows.map((row) => (
                    <div key={row.id} className="fees-withdrawal-card-item">
                      <div className="fees-withdrawal-card-title">{row.asset}</div>
                      <div className="fees-withdrawal-card-row">
                        <span className="fees-withdrawal-label">Maker Fee</span>
                        <span className="fees-withdrawal-value">{row.makerFee}</span>
                      </div>
                      <div className="fees-withdrawal-card-row">
                        <span className="fees-withdrawal-label">Taker Fee</span>
                        <span className="fees-withdrawal-value">{row.takerFee}</span>
                      </div>
                      <div className="fees-withdrawal-card-row">
                        <span className="fees-withdrawal-label">Min / Max Deposit</span>
                        <span className="fees-withdrawal-value">{row.minDeposit} — {row.maxDeposit}</span>
                      </div>
                      <div className="fees-withdrawal-card-row">
                        <span className="fees-withdrawal-label">Min / Max Withdrawal</span>
                        <span className="fees-withdrawal-value">{row.minWithdrawal} — {row.maxWithdrawal}</span>
                      </div>
                      <div className="fees-withdrawal-card-row">
                        <span className="fees-withdrawal-label">Withdrawal Fee</span>
                        <span className="fees-withdrawal-value fee-highlight-charge">{row.withdrawalFee}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="favouriteData text-center py-4">
                    <img src="/images/no-data.svg" className="img-fluid" width="96" height="96" alt="" />
                    <p className="mt-2" style={{ color: "#848e9c" }}>No Data Found</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* FAQ Section */}
          <div className="fees-faq-container">
            <div className="fees-faq-header">
              <h3 className="fees-faq-title">How fees work on AGCX</h3>
              <p className="fees-faq-subtitle">
                The background on how your tier is set, how maker and taker orders differ, and how the numbers above are actually calculated.
              </p>
            </div>

            <div className="fees-faq-list">
              {FAQ_ITEMS.map((item) => {
                const isOpen = Boolean(openFaqs[item.id]);
                return (
                  <div key={item.id} className="fees-faq-item">
                    <button
                      className="fees-faq-question-btn"
                      onClick={() => toggleFaq(item.id)}
                      aria-expanded={isOpen}
                    >
                      <span className="fees-faq-question-text">{item.question}</span>
                      <i className={`fees-faq-icon ${isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}></i>
                    </button>
                    {isOpen && <div className="fees-faq-answer">{item.content}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default FeesPage;
